import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  GraduationCap, Users, BookOpen, ClipboardList, Plus, Eye,
  ArrowLeft, Loader2, Calendar, Target, TrendingUp, Award,
  Clock, CheckCircle2, AlertCircle, BarChart3, Video, Link2,
  Trash2, Sparkles, FileText, Play, ExternalLink, X, Library
} from 'lucide-react';

const TeacherDashboard = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Assignment creation
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    title: '',
    description: '',
    class_id: '',
    subject: '',
    due_date: ''
  });
  const [creatingAssignment, setCreatingAssignment] = useState(false);

  // Activities for assignment
  const [availableActivities, setAvailableActivities] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  
  // Custom Lessons state
  const [customLessons, setCustomLessons] = useState([]);
  const [showLessonDialog, setShowLessonDialog] = useState(false);
  const [lessonForm, setLessonForm] = useState({
    title: '',
    subject: '',
    grade_level: '',
    description: '',
    content: '',
    video_links: [],
    learning_objectives: [],
    auto_generate_assessment: false
  });
  const [newVideoLink, setNewVideoLink] = useState('');
  const [newObjective, setNewObjective] = useState('');
  const [creatingLesson, setCreatingLesson] = useState(false);
  const [generatingAssessment, setGeneratingAssessment] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    fetchCustomLessons();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/teacher/dashboard');
      setDashboardData(response.data);
      if (response.data.classes.length > 0) {
        setSelectedClass(response.data.classes[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Teacher role required.');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomLessons = async () => {
    try {
      const response = await api.get('/teacher/lessons');
      setCustomLessons(response.data.lessons || []);
    } catch (error) {
      console.error('Failed to fetch custom lessons:', error);
    }
  };

  const fetchActivities = async (subject) => {
    try {
      const response = await api.get(`/activities?subject=${subject}`);
      setAvailableActivities(response.data);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    }
  };

  const handleSubjectChange = (subject) => {
    setAssignmentForm(prev => ({ ...prev, subject }));
    setSelectedActivities([]);
    if (subject) {
      fetchActivities(subject);
    }
  };

  const toggleActivity = (activityId) => {
    setSelectedActivities(prev =>
      prev.includes(activityId)
        ? prev.filter(id => id !== activityId)
        : [...prev, activityId]
    );
  };

  const createAssignment = async () => {
    if (!assignmentForm.title || !assignmentForm.class_id) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setCreatingAssignment(true);
    try {
      await api.post('/assignments', {
        ...assignmentForm,
        activity_ids: selectedActivities,
        due_date: assignmentForm.due_date || null
      });
      toast.success('Assignment created!');
      setShowAssignDialog(false);
      setAssignmentForm({ title: '', description: '', class_id: '', subject: '', due_date: '' });
      setSelectedActivities([]);
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to create assignment');
    } finally {
      setCreatingAssignment(false);
    }
  };

  // Custom Lesson Functions
  const addVideoLink = () => {
    if (newVideoLink.trim()) {
      // Convert YouTube URL to embed format if needed
      let embedUrl = newVideoLink.trim();
      if (embedUrl.includes('youtube.com/watch')) {
        const videoId = new URL(embedUrl).searchParams.get('v');
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      } else if (embedUrl.includes('youtu.be/')) {
        const videoId = embedUrl.split('youtu.be/')[1]?.split('?')[0];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      }
      setLessonForm(prev => ({
        ...prev,
        video_links: [...prev.video_links, embedUrl]
      }));
      setNewVideoLink('');
    }
  };

  const removeVideoLink = (index) => {
    setLessonForm(prev => ({
      ...prev,
      video_links: prev.video_links.filter((_, i) => i !== index)
    }));
  };

  const addObjective = () => {
    if (newObjective.trim()) {
      setLessonForm(prev => ({
        ...prev,
        learning_objectives: [...prev.learning_objectives, newObjective.trim()]
      }));
      setNewObjective('');
    }
  };

  const removeObjective = (index) => {
    setLessonForm(prev => ({
      ...prev,
      learning_objectives: prev.learning_objectives.filter((_, i) => i !== index)
    }));
  };

  const createCustomLesson = async () => {
    if (!lessonForm.title || !lessonForm.subject) {
      toast.error('Please fill in title and subject');
      return;
    }
    
    setCreatingLesson(true);
    try {
      const response = await api.post('/teacher/lessons', lessonForm);
      toast.success('Custom lesson created!');
      setShowLessonDialog(false);
      setLessonForm({
        title: '',
        subject: '',
        grade_level: '',
        description: '',
        content: '',
        video_links: [],
        learning_objectives: [],
        auto_generate_assessment: false
      });
      fetchCustomLessons();
      
      if (lessonForm.auto_generate_assessment && response.data.lesson?.assessment) {
        toast.success('Adaptive assessment auto-generated!');
      }
    } catch (error) {
      toast.error('Failed to create lesson');
    } finally {
      setCreatingLesson(false);
    }
  };

  const generateAssessmentForLesson = async (lessonId) => {
    setGeneratingAssessment(lessonId);
    try {
      await api.post(`/teacher/lessons/${lessonId}/generate-assessment`);
      toast.success('Adaptive assessment generated!');
      fetchCustomLessons();
    } catch (error) {
      toast.error('Failed to generate assessment');
    } finally {
      setGeneratingAssessment(null);
    }
  };

  const deleteLesson = async (lessonId) => {
    if (!confirm('Are you sure you want to delete this lesson?')) return;
    try {
      await api.delete(`/teacher/lessons/${lessonId}`);
      toast.success('Lesson deleted');
      fetchCustomLessons();
    } catch (error) {
      toast.error('Failed to delete lesson');
    }
  };

  const getStudentsForClass = (classId) => {
    if (!dashboardData) return [];
    return dashboardData.students.filter(s => 
      dashboardData.classes.find(c => c.id === classId)?.student_ids?.includes(s.id)
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-500">Failed to load dashboard</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-xl text-slate-800">Teacher Dashboard</h1>
                  <p className="text-sm text-slate-500">{dashboardData.teacher.name}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Link to="/lessons/library">
                <Button variant="outline" data-testid="teacher-lesson-library-btn">
                  <Library className="w-4 h-4 mr-2" />
                  Lesson Library
                </Button>
              </Link>
              <Link to="/teacher/google-classroom">
                <Button variant="outline" data-testid="google-classroom-btn">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Google Classroom
                </Button>
              </Link>
              <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-assignment-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Assignment
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Assignment</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Assignment Title *</Label>
                      <Input
                        placeholder="e.g., Math Week 1"
                        value={assignmentForm.title}
                        onChange={(e) => setAssignmentForm(prev => ({ ...prev, title: e.target.value }))}
                        data-testid="assignment-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Assign to Class *</Label>
                      <Select
                        value={assignmentForm.class_id}
                        onValueChange={(v) => setAssignmentForm(prev => ({ ...prev, class_id: v }))}
                      >
                        <SelectTrigger data-testid="assignment-class-select">
                          <SelectValue placeholder="Select class" />
                        </SelectTrigger>
                        <SelectContent>
                          {dashboardData.classes.map(cls => (
                            <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Subject</Label>
                      <Select
                        value={assignmentForm.subject}
                        onValueChange={handleSubjectChange}
                      >
                        <SelectTrigger data-testid="assignment-subject-select">
                          <SelectValue placeholder="Select subject" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="reading">Reading/ELA</SelectItem>
                          <SelectItem value="math">Math</SelectItem>
                          <SelectItem value="alphabets">Alphabets</SelectItem>
                          <SelectItem value="counting">Counting</SelectItem>
                          <SelectItem value="writing">Writing</SelectItem>
                          <SelectItem value="science">Science</SelectItem>
                          <SelectItem value="christian">Christian/Bible</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Due Date (Optional)</Label>
                      <Input
                        type="date"
                        value={assignmentForm.due_date}
                        onChange={(e) => setAssignmentForm(prev => ({ ...prev, due_date: e.target.value }))}
                        data-testid="assignment-due-date"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Description (Optional)</Label>
                    <Textarea
                      placeholder="Instructions for students..."
                      value={assignmentForm.description}
                      onChange={(e) => setAssignmentForm(prev => ({ ...prev, description: e.target.value }))}
                      data-testid="assignment-description"
                    />
                  </div>
                  
                  {availableActivities.length > 0 && (
                    <div className="space-y-2">
                      <Label>Select Activities</Label>
                      <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-2">
                        {availableActivities.map(activity => (
                          <div
                            key={activity.id}
                            onClick={() => toggleActivity(activity.id)}
                            className={`p-3 rounded-lg cursor-pointer transition-all ${
                              selectedActivities.includes(activity.id)
                                ? 'bg-blue-100 border-2 border-blue-500'
                                : 'bg-slate-50 hover:bg-slate-100'
                            }`}
                          >
                            <p className="font-medium text-sm">{activity.title}</p>
                            <p className="text-xs text-slate-500">Level {activity.level} | {activity.xp_reward} XP</p>
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-slate-500">{selectedActivities.length} activities selected</p>
                    </div>
                  )}
                  
                  <Button
                    onClick={createAssignment}
                    disabled={creatingAssignment}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="submit-assignment-btn"
                  >
                    {creatingAssignment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Create Assignment
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid sm:grid-cols-4 gap-4 mb-8">
          <Card 
            className="cursor-pointer hover:shadow-md transition-all" 
            onClick={() => setActiveTab('classes')}
            data-testid="classes-stat-widget"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">My Classes</p>
                  <p className="text-2xl font-bold text-slate-900">{dashboardData.stats.classes_count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-md transition-all" 
            onClick={() => setActiveTab('students')}
            data-testid="students-stat-widget"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Students</p>
                  <p className="text-2xl font-bold text-slate-900">{dashboardData.stats.students_count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-md transition-all" 
            onClick={() => setActiveTab('assignments')}
            data-testid="assignments-stat-widget"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <ClipboardList className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Active Assignments</p>
                  <p className="text-2xl font-bold text-slate-900">{dashboardData.stats.assignments_count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-md transition-all" 
            onClick={() => setActiveTab('progress')}
            data-testid="activity-stat-widget"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Recent Activity</p>
                  <p className="text-2xl font-bold text-slate-900">{dashboardData.recent_progress.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-100">
              Overview
            </TabsTrigger>
            <TabsTrigger value="classes" className="data-[state=active]:bg-blue-100">
              My Classes
            </TabsTrigger>
            <TabsTrigger value="students" className="data-[state=active]:bg-blue-100">
              Students
            </TabsTrigger>
            <TabsTrigger value="assignments" className="data-[state=active]:bg-blue-100">
              Assignments
            </TabsTrigger>
            <TabsTrigger value="lessons" className="data-[state=active]:bg-green-100">
              Custom Lessons
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* My Classes */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    My Classes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dashboardData.classes.map(cls => (
                      <div
                        key={cls.id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                      >
                        <div>
                          <p className="font-medium">{cls.name}</p>
                          <p className="text-sm text-slate-500">
                            {cls.student_ids?.length || 0} students
                            {cls.grade_level && ` | ${cls.grade_level}`}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedClass(cls.id);
                            setActiveTab('students');
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </div>
                    ))}
                    {dashboardData.classes.length === 0 && (
                      <p className="text-center py-8 text-slate-500">No classes assigned yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Student Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                    Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {dashboardData.recent_progress.slice(0, 10).map((progress, idx) => {
                      const student = dashboardData.students.find(s => s.id === progress.child_id);
                      return (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            progress.completed ? 'bg-green-100' : 'bg-amber-100'
                          }`}>
                            {progress.completed ? (
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                            ) : (
                              <Clock className="w-4 h-4 text-amber-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{student?.name || 'Unknown'}</p>
                            <p className="text-xs text-slate-500">
                              Activity: {progress.activity_id}
                            </p>
                          </div>
                          {progress.score !== null && (
                            <span className="text-sm font-medium text-blue-600">
                              {progress.score}%
                            </span>
                          )}
                        </div>
                      );
                    })}
                    {dashboardData.recent_progress.length === 0 && (
                      <p className="text-center py-8 text-slate-500">No recent activity</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Active Assignments */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-purple-500" />
                    Active Assignments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboardData.active_assignments.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead>Class</TableHead>
                          <TableHead>Subject</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dashboardData.active_assignments.map(assignment => {
                          const cls = dashboardData.classes.find(c => c.id === assignment.class_id);
                          return (
                            <TableRow key={assignment.id}>
                              <TableCell className="font-medium">{assignment.title}</TableCell>
                              <TableCell>{cls?.name || '-'}</TableCell>
                              <TableCell className="capitalize">{assignment.subject || '-'}</TableCell>
                              <TableCell>
                                {assignment.due_date 
                                  ? new Date(assignment.due_date).toLocaleDateString()
                                  : '-'}
                              </TableCell>
                              <TableCell>
                                <Button size="sm" variant="outline">View</Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-center py-8 text-slate-500">No active assignments</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Classes Tab */}
          <TabsContent value="classes">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {dashboardData.classes.map(cls => (
                <Card key={cls.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle>{cls.name}</CardTitle>
                    <CardDescription>
                      {cls.grade_level && `${cls.grade_level} | `}
                      {cls.student_ids?.length || 0} students
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {cls.description && (
                        <p className="text-sm text-slate-600">{cls.description}</p>
                      )}
                      <div className="flex gap-2">
                        <Button
                          className="flex-1"
                          onClick={() => {
                            setSelectedClass(cls.id);
                            setActiveTab('students');
                          }}
                        >
                          <Users className="w-4 h-4 mr-2" />
                          View Students
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Students</CardTitle>
                  <Select value={selectedClass || ''} onValueChange={setSelectedClass}>
                    <SelectTrigger className="w-48" data-testid="class-filter-select">
                      <SelectValue placeholder="Filter by class" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Classes</SelectItem>
                      {dashboardData.classes.map(cls => (
                        <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Class</TableHead>
                      <TableHead>Level</TableHead>
                      <TableHead>XP</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(selectedClass && selectedClass !== 'all'
                      ? getStudentsForClass(selectedClass)
                      : dashboardData.students
                    ).map(student => {
                      const studentClass = dashboardData.classes.find(c => 
                        c.student_ids?.includes(student.id)
                      );
                      return (
                        <TableRow key={student.id}>
                          <TableCell className="font-medium">{student.name}</TableCell>
                          <TableCell>{studentClass?.name || '-'}</TableCell>
                          <TableCell>
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                              Level {student.current_level || 1}
                            </span>
                          </TableCell>
                          <TableCell>{student.total_xp || 0}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Award className="w-4 h-4 text-amber-500" />
                              <span>{student.badges?.length || 0} badges</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Link to={`/learn/${student.id}`}>
                              <Button size="sm" variant="outline">
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {dashboardData.students.length === 0 && (
                  <p className="text-center py-8 text-slate-500">No students in your classes</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Assignments Tab */}
          <TabsContent value="assignments">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>All Assignments</CardTitle>
                  <Button
                    onClick={() => setShowAssignDialog(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Assignment
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {dashboardData.active_assignments.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Class</TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>Activities</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboardData.active_assignments.map(assignment => {
                        const cls = dashboardData.classes.find(c => c.id === assignment.class_id);
                        return (
                          <TableRow key={assignment.id}>
                            <TableCell className="font-medium">{assignment.title}</TableCell>
                            <TableCell>{cls?.name || '-'}</TableCell>
                            <TableCell className="capitalize">{assignment.subject || '-'}</TableCell>
                            <TableCell>{assignment.activity_ids?.length || 0}</TableCell>
                            <TableCell>
                              {assignment.due_date 
                                ? new Date(assignment.due_date).toLocaleDateString()
                                : '-'}
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                assignment.status === 'active' 
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-slate-100 text-slate-600'
                              }`}>
                                {assignment.status}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Button size="sm" variant="outline">View Progress</Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12">
                    <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">No assignments yet</p>
                    <Button onClick={() => setShowAssignDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Your First Assignment
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Custom Lessons Tab */}
          <TabsContent value="lessons">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-green-500" />
                      Custom Lessons
                    </CardTitle>
                    <CardDescription>Create your own lessons with videos and AI-generated assessments</CardDescription>
                  </div>
                  <Dialog open={showLessonDialog} onOpenChange={setShowLessonDialog}>
                    <DialogTrigger asChild>
                      <Button className="bg-green-600 hover:bg-green-700" data-testid="create-lesson-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Lesson
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Create Custom Lesson</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        {/* Basic Info */}
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Lesson Title *</Label>
                            <Input
                              placeholder="e.g., Introduction to Fractions"
                              value={lessonForm.title}
                              onChange={(e) => setLessonForm(prev => ({ ...prev, title: e.target.value }))}
                              data-testid="lesson-title-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Subject *</Label>
                            <Select
                              value={lessonForm.subject}
                              onValueChange={(v) => setLessonForm(prev => ({ ...prev, subject: v }))}
                            >
                              <SelectTrigger data-testid="lesson-subject-select">
                                <SelectValue placeholder="Select subject" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="reading">Reading/ELA</SelectItem>
                                <SelectItem value="math">Math</SelectItem>
                                <SelectItem value="writing">Writing</SelectItem>
                                <SelectItem value="science">Science</SelectItem>
                                <SelectItem value="christian">Christian/Bible</SelectItem>
                                <SelectItem value="spanish">Spanish</SelectItem>
                                <SelectItem value="french">French</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Grade Level</Label>
                          <Select
                            value={lessonForm.grade_level}
                            onValueChange={(v) => setLessonForm(prev => ({ ...prev, grade_level: v }))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select grade level" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="K">Kindergarten</SelectItem>
                              <SelectItem value="1">Grade 1</SelectItem>
                              <SelectItem value="2">Grade 2</SelectItem>
                              <SelectItem value="3">Grade 3</SelectItem>
                              <SelectItem value="4">Grade 4</SelectItem>
                              <SelectItem value="5">Grade 5</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Description</Label>
                          <Textarea
                            placeholder="Brief overview of what students will learn..."
                            value={lessonForm.description}
                            onChange={(e) => setLessonForm(prev => ({ ...prev, description: e.target.value }))}
                            rows={2}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Lesson Content *</Label>
                          <Textarea
                            placeholder="Enter the main lesson content, teaching material, examples..."
                            value={lessonForm.content}
                            onChange={(e) => setLessonForm(prev => ({ ...prev, content: e.target.value }))}
                            rows={6}
                            data-testid="lesson-content-textarea"
                          />
                        </div>

                        {/* Learning Objectives */}
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Target className="w-4 h-4 text-blue-500" />
                            Learning Objectives
                          </Label>
                          <div className="flex gap-2">
                            <Input
                              placeholder="e.g., Students will be able to add fractions"
                              value={newObjective}
                              onChange={(e) => setNewObjective(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addObjective())}
                            />
                            <Button type="button" variant="outline" onClick={addObjective}>
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          {lessonForm.learning_objectives.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {lessonForm.learning_objectives.map((obj, idx) => (
                                <span key={idx} className="flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                                  {obj}
                                  <button onClick={() => removeObjective(idx)} className="hover:text-red-500">
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* YouTube Videos */}
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Video className="w-4 h-4 text-red-500" />
                            YouTube Videos
                          </Label>
                          <div className="flex gap-2">
                            <Input
                              placeholder="Paste YouTube URL (e.g., https://www.youtube.com/watch?v=...)"
                              value={newVideoLink}
                              onChange={(e) => setNewVideoLink(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addVideoLink())}
                            />
                            <Button type="button" variant="outline" onClick={addVideoLink}>
                              <Link2 className="w-4 h-4" />
                            </Button>
                          </div>
                          {lessonForm.video_links.length > 0 && (
                            <div className="space-y-2 mt-2">
                              {lessonForm.video_links.map((url, idx) => (
                                <div key={idx} className="flex items-center gap-2 bg-slate-100 p-2 rounded-lg">
                                  <Play className="w-4 h-4 text-red-500 flex-shrink-0" />
                                  <span className="text-sm truncate flex-1">{url}</span>
                                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700">
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                  <button onClick={() => removeVideoLink(idx)} className="text-red-500 hover:text-red-700">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <p className="text-xs text-slate-500">Add educational YouTube videos to enhance your lesson</p>
                        </div>

                        {/* Auto-generate Assessment */}
                        <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                          <input
                            type="checkbox"
                            id="auto-assessment"
                            checked={lessonForm.auto_generate_assessment}
                            onChange={(e) => setLessonForm(prev => ({ ...prev, auto_generate_assessment: e.target.checked }))}
                            className="w-5 h-5 accent-purple-600"
                          />
                          <label htmlFor="auto-assessment" className="flex-1 cursor-pointer">
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-4 h-4 text-purple-500" />
                              <span className="font-medium text-purple-800">Auto-generate Adaptive Assessment</span>
                            </div>
                            <p className="text-sm text-purple-600 mt-1">AI will create quiz questions based on your lesson content</p>
                          </label>
                        </div>

                        <Button
                          onClick={createCustomLesson}
                          disabled={creatingLesson}
                          className="w-full bg-green-600 hover:bg-green-700"
                          data-testid="submit-lesson-btn"
                        >
                          {creatingLesson ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Create Lesson
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {customLessons.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {customLessons.map(lesson => (
                      <Card key={lesson.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-semibold text-lg">{lesson.title}</h3>
                              <p className="text-sm text-slate-500 capitalize">{lesson.subject} {lesson.grade_level && `• Grade ${lesson.grade_level}`}</p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              lesson.is_published ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {lesson.is_published ? 'Published' : 'Draft'}
                            </span>
                          </div>
                          
                          {lesson.description && (
                            <p className="text-sm text-slate-600 mb-3 line-clamp-2">{lesson.description}</p>
                          )}
                          
                          {/* Video count badge */}
                          {lesson.video_links?.length > 0 && (
                            <div className="flex items-center gap-1 text-red-500 text-sm mb-2">
                              <Video className="w-4 h-4" />
                              <span>{lesson.video_links.length} video{lesson.video_links.length > 1 ? 's' : ''}</span>
                            </div>
                          )}
                          
                          {/* Assessment badge */}
                          {lesson.assessment && (
                            <div className="flex items-center gap-1 text-purple-500 text-sm mb-3">
                              <CheckCircle2 className="w-4 h-4" />
                              <span>Assessment ready ({lesson.assessment.questions?.length || 0} questions)</span>
                            </div>
                          )}
                          
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" className="flex-1">
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            {!lesson.assessment && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => generateAssessmentForLesson(lesson.id)}
                                disabled={generatingAssessment === lesson.id}
                                className="flex-1 border-purple-300 text-purple-600 hover:bg-purple-50"
                              >
                                {generatingAssessment === lesson.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                ) : (
                                  <Sparkles className="w-4 h-4 mr-1" />
                                )}
                                Generate Quiz
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => deleteLesson(lesson.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-2">No custom lessons yet</p>
                    <p className="text-sm text-slate-400 mb-4">Create your own lessons with videos and AI-powered assessments</p>
                    <Button onClick={() => setShowLessonDialog(true)} className="bg-green-600 hover:bg-green-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Your First Lesson
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default TeacherDashboard;
